import { regions, genres, yearRanges, contentTypes } from "@/mocks/movies";

export interface CategoryFilters {
  type: string;
  region: string;
  genre: string;
  year: string;
}

interface FilterPanelProps {
  filters: CategoryFilters;
  onFilterChange: (key: keyof CategoryFilters, value: string) => void;
  onReset: () => void;
  resultCount: number;
  open: boolean;
  onToggle: () => void;
}

interface FilterOption {
  label: string;
  value: string;
}

const toOptions = (list: readonly string[]): FilterOption[] =>
  list.map((item) => ({ label: item, value: item }));

const groups: { key: keyof CategoryFilters; label: string; options: FilterOption[] }[] = [
  { key: "type", label: "类型", options: toOptions(contentTypes) },
  { key: "region", label: "地区", options: toOptions(regions) },
  { key: "genre", label: "题材", options: toOptions(genres) },
  { key: "year", label: "年份", options: yearRanges.map((item) => ({ label: item.label, value: item.value })) },
];

export default function FilterPanel({ filters, onFilterChange, onReset, resultCount, open, onToggle }: FilterPanelProps) {
  const hasActiveFilter =
    filters.type !== "全部" || filters.region !== "全部" || filters.genre !== "全部" || filters.year !== "all";

  return (
    <div className="rounded-lg border border-background-200 bg-background-100">
      <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6 md:py-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-950 text-background-50">
            <i className="ri-filter-3-line text-base" />
          </span>
          <div>
            <p className="text-sm font-medium text-foreground-950">筛选条件</p>
            <p className="text-[11px] text-foreground-500">当前匹配 {resultCount} 部作品</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilter && (
            <button
              type="button"
              onClick={onReset}
              className="hidden items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600 cursor-pointer sm:inline-flex whitespace-nowrap"
            >
              <i className="ri-refresh-line w-3.5 h-3.5 flex items-center justify-center" />
              重置
            </button>
          )}
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 cursor-pointer md:hidden"
            aria-expanded={open}
          >
            {open ? "收起" : "展开"}
            <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} w-3.5 h-3.5 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} border-t border-background-200 px-4 py-4 md:block md:px-6 md:py-5`}>
        <div className="space-y-4">
          {groups.map((group) => {
            const options = group.options;
            return (
              <div key={group.key} className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
                <span className="shrink-0 pt-1.5 text-xs font-medium text-foreground-500 sm:w-12">{group.label}</span>
                <div className="flex flex-wrap gap-2">
                  {options.map((option) => {
                    const active = filters[group.key] === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onFilterChange(group.key, option.value)}
                        className={`rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                          active
                            ? "bg-primary-950 text-background-50 font-medium"
                            : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-800"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {hasActiveFilter && (
          <button
            type="button"
            onClick={onReset}
            className="mt-4 inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 cursor-pointer sm:hidden"
          >
            <i className="ri-refresh-line w-3.5 h-3.5 flex items-center justify-center" />
            重置全部筛选
          </button>
        )}
      </div>
    </div>
  );
}